import React, { useEffect, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, Pencil, RotateCcw, Save } from "lucide-react";
import GoalPhotoUpload from "./GoalPhotoUpload";
import { BACKEND_URL } from "@/lib/api";

const parseYmd = (s) => {
  if (!s) return undefined;
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const Field = ({ label, children }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-sm">{children || "—"}</p>
  </div>
);

// View / edit a single record and flip it to Resolved.
export const GoalRecordDetail = ({ recordId, open, onOpenChange, categories, sources, sourceTypes = [], managers, canEdit, onChanged }) => {
  const [rec, setRec] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [calOpen, setCalOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await staffApi.get(`/goal/records/${recordId}`);
      setRec(r.data);
      setForm({
        title: r.data.title || "",
        category_id: r.data.category_id || "",
        date_reported: r.data.date_reported || "",
        source: r.data.source || "",
        source_type: r.data.source_type || "",
        description: r.data.description || "",
        assigned_to_id: r.data.assigned_to_id || "",
        resolution_note: r.data.resolution_note || "",
        photos: Array.isArray(r.data.photos) ? r.data.photos : [],
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (!open || !recordId) return;
    setEditing(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recordId]);

  const patch = async (body, msg) => {
    setBusy(true);
    try {
      const r = await staffApi.put(`/goal/records/${recordId}`, body);
      setRec(r.data);
      setEditing(false);
      toast.success(msg);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!rec) return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl"><p className="text-sm text-muted-foreground py-8 text-center">Loading…</p></DialogContent>
    </Dialog>
  );

  const resolved = rec.status === "resolved";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="goal-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {rec.title || rec.customer_name}
            <Badge
              variant={resolved ? "secondary" : "destructive"}
              className="text-[10px]"
              data-testid="goal-detail-status"
            >
              {resolved ? "Resolved" : "Unresolved"}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            {rec.customer_name} · {rec.treatment_name} · {rec.treatment_date} · {rec.branch_name}
          </DialogDescription>
        </DialogHeader>

        {editing ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="h-10"
                data-testid="goal-detail-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm((f) => ({ ...f, category_id: v }))}>
                  <SelectTrigger data-testid="goal-detail-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Date reported</Label>
                <div className="flex gap-2">
                  <Input value={form.date_reported} readOnly className="h-10" data-testid="goal-detail-date" />
                  <Popover open={calOpen} onOpenChange={setCalOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="icon" className="h-10 w-10 shrink-0"><CalendarDays className="w-4 h-4" /></Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="single"
                        selected={parseYmd(form.date_reported)}
                        onSelect={(d) => {
                          if (!d) return;
                          setForm((f) => ({ ...f, date_reported: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` }));
                          setCalOpen(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div>
                <Label className="text-xs">Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm((f) => ({ ...f, source: v }))}>
                  <SelectTrigger data-testid="goal-detail-source"><SelectValue /></SelectTrigger>
                  <SelectContent>{sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Assigned to</Label>
                <Select value={form.assigned_to_id} onValueChange={(v) => setForm((f) => ({ ...f, assigned_to_id: v }))}>
                  <SelectTrigger data-testid="goal-detail-assignee"><SelectValue placeholder="Management team member" /></SelectTrigger>
                  <SelectContent>
                    {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.role_name ? ` · ${m.role_name}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Source type</Label>
              <Select value={form.source_type} onValueChange={(v) => setForm((f) => ({ ...f, source_type: v }))}>
                <SelectTrigger data-testid="goal-detail-source-type"><SelectValue placeholder="Which kind of complaint is this?" /></SelectTrigger>
                <SelectContent>{sourceTypes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Opportunity Description</Label>
              <Textarea
                rows={4} className="resize-none"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                data-testid="goal-detail-description"
              />
            </div>
            <div>
              <Label className="text-xs">Action taken / resolution note</Label>
              <Textarea
                rows={3} className="resize-none"
                value={form.resolution_note}
                onChange={(e) => setForm((f) => ({ ...f, resolution_note: e.target.value }))}
                data-testid="goal-detail-resolution-note"
              />
            </div>
            <div>
              <Label className="text-xs">Photos</Label>
              <GoalPhotoUpload
                photos={form.photos || []}
                onChange={(next) => setForm((f) => ({ ...f, photos: next }))}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">{rec.category_name}</Field>
              <Field label="Date reported">{rec.date_reported}</Field>
              <Field label="Source">{rec.source}</Field>
              <Field label="Source type">{rec.source_type}</Field>
              <Field label="Assigned to">{rec.assigned_to_name}</Field>
              <Field label="WhatsApp">{rec.customer_whatsapp}</Field>
              <Field label="Email">{rec.customer_email}</Field>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Opportunity Description</p>
              <p className="text-sm whitespace-pre-wrap" data-testid="goal-detail-description-text">{rec.description || "—"}</p>
            </div>
            {rec.resolution_note && (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Action taken</p>
                <p className="text-sm whitespace-pre-wrap">{rec.resolution_note}</p>
              </div>
            )}
            {(rec.photos || []).length > 0 && (
              <div data-testid="goal-detail-photos">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Photos</p>
                <div className="flex flex-wrap gap-2">
                  {rec.photos.map((p) => (
                    <a
                      key={p.id}
                      href={p.url?.startsWith("http") ? p.url : `${BACKEND_URL}${p.url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="w-20 h-20 rounded-md overflow-hidden border border-border block"
                      data-testid={`goal-detail-photo-${p.id}`}
                    >
                      <img
                        src={p.url?.startsWith("http") ? p.url : `${BACKEND_URL}${p.url}`}
                        alt={p.filename || "photo"}
                        className="w-full h-full object-cover"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Logged by {rec.created_by_name} · {String(rec.created_at || "").slice(0, 10)}
              {resolved && rec.resolved_by_name ? ` · Resolved by ${rec.resolved_by_name} on ${String(rec.resolved_at || "").slice(0, 10)}` : ""}
            </p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {canEdit && !editing && (
            <Button variant="outline" onClick={() => setEditing(true)} data-testid="goal-detail-edit-btn">
              <Pencil className="w-4 h-4 mr-1" /> Edit
            </Button>
          )}
          {canEdit && editing && (
            <Button disabled={busy} onClick={() => patch(form, "Record updated")} data-testid="goal-detail-save-btn">
              <Save className="w-4 h-4 mr-1" /> Save changes
            </Button>
          )}
          {canEdit && !editing && (
            resolved ? (
              <Button variant="outline" disabled={busy} onClick={() => patch({ status: "unresolved" }, "Record reopened")} data-testid="goal-detail-reopen-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Reopen
              </Button>
            ) : (
              <Button disabled={busy} onClick={() => patch({ status: "resolved", resolution_note: form.resolution_note }, "Marked as resolved")} data-testid="goal-detail-resolve-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Mark as Resolved
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GoalRecordDetail;
